import React, { createContext, useContext, useState, useEffect, use } from "react";
import { useStaticQuery, graphql } from "gatsby";
import {
	nodesConfigToObject,
	flattenNodes,
	isIframe,
	fetchUserData,
	navDataFormat,
	findIfRestricted,
	findIfRestrictedStatic,
	getQueryNode,
	filterPrivatePageContext,
	findLocalCartItems,
} from "./..";
import { AuthContext } from "./components/AuthContext";

export const SiteContext = createContext();

const defaultSortOptions = require("./../sortOptions.json");

/**
 * Site: A site wide wrapper to process general data and user authentication ...
 * ... and make it globally available via SiteContext
 * @param props.element: The page's DOM
 * @param props.props: Gatsby page props like pageContext, serverData, path, location etc.
 * @param props.options: Gatsby plugin options as defined in site's gatsby-config
 * @returns
 */
function Site({ element, props, options }) {
	const configs = nodesConfigToObject(options.nodesConfig);
	const themeConfig =
		!process.env.CLF_WHITELABEL && options.themeConfig ? options.themeConfig : {};
	const bookmarkableBundles =
		options.bookmarkableBundles && Array.isArray(options.bookmarkableBundles)
			? options.bookmarkableBundles
			: [];
	const siteKey = options.siteKey ? options.siteKey : "default";
	const { pageContext, path, location, serverData } = props;
	const _query_node = getQueryNode(location.search);
	const _pageContext = _query_node
		? filterPrivatePageContext(pageContext)
		: { ...pageContext };
	const authContext = useContext(AuthContext);

	const [
		{ isAuthenticated },
		{ dispatch, logout, checkAuth, isLoggedIn },
		[userData, setUserData],
	] = authContext;

	const pageData = { ..._pageContext, ...serverData };
	const isPrivateLayout = isAuthenticated && pageData.private;
	

	const _restricted = findIfRestrictedStatic(pageData);
	const [isRestricted, setIsRestricted] = useState(_restricted);
	const [cartItems, setCartItems] = useState(findLocalCartItems());
	const _isIframe = isIframe();
	const [previewing, setPreviewing] = useState(_isIframe);

	let sortOptions = options.sortOptions ? options.sortOptions : defaultSortOptions;
	let scoreSortOption = sortOptions.find((option) => option.value === "_score");
	if (!scoreSortOption) {
		sortOptions.push({
			value: "_score",
			label: "Relevance",
			direction: "desc",
		});
	}

	const data = useStaticQuery(graphql`
		query {
			site {
				siteMetadata {
					title
					subtitle
					titleAsText
					url
					drupalUrl
					privateRoute
					twitterUsername
					facebookUrl
					linkedInUrl
					youtubeUrl
					instagramUrl
					hasSearch
					hasCart
					cartRoute
					colophonText
				}
			}
			sitemap: allSitemapRef {
				edges {
					node {
						id
						nid
						slug
					}
				}
			}
			bundles: allBundleRef {
				edges {
					node {
						bundle
					}
				}
			}
			layouts: allLayoutRef {
				edges {
					node {
						id
						types
					}
				}
			}
			type_definitions: allTypeDefinitionRef {
				edges {
					node {
						name
						bundle
					}
				}
			}
			alert_banners: allAlertBannerRef {
				edges {
					node {
						id
						name
						show_alert
						private_alert
						description
						severity
					}
				}
			}
			cookie: allCookieRef {
				edges {
					node {
						id
						showflag
						texts {
							value
						}
						subtitle
					}
				}
			}
			footer: allFooterRef {
				edges {
					node {
						id
						texts {
							value
						}
					}
				}
			}
			mainMenu: allMenuItems(filter: { menu_name: { eq: "main" } }) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
							childrenMenuItems {
								title
								url
								id
								weight
								route {
									parameters {
										node
									}
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			secondaryMenu: allMenuItems(filter: { menu_name: { eq: "tools" } }) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			footerMenu: allMenuItems(filter: { menu_name: { eq: "footer" } }) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
			privateMenu: allMenuItems(filter: { menu_name: { eq: "account" } }) {
				edges {
					node {
						menu_name
						url
						title
						id
						weight
						childrenMenuItems {
							title
							url
							id
							weight
							route {
								parameters {
									node
								}
							}
							childrenMenuItems {
								title
								url
								id
								weight
								route {
									parameters {
										node
									}
								}
							}
						}
						parent {
							id
						}
						route {
							parameters {
								node
							}
						}
					}
				}
			}
		}
	`);

	/**
	 * define general site properties
	 */
	const sitemap = flattenNodes(data.sitemap, "all", "object");
	const sitemap_arr = flattenNodes(data.sitemap, "all");
	const bundles = flattenNodes(data.bundles, ["bundle"]).map((b) => b.bundle);
	const layouts = flattenNodes(data.layouts, "all");
	const typeDefinitions = flattenNodes(data.type_definitions, "all");

	const alertBanners =
		data.alert_banners &&
		Array.isArray(data.alert_banners.edges) &&
		data.alert_banners.edges.length > 0
			? data.alert_banners.edges.map(({ node }) => ({
					...node,
			  }))
			: null;

	const footerData =
		data.footer.edges &&
		data.footer.edges[0] &&
		data.footer.edges[0].node &&
		data.footer.edges[0].node.texts
			? data.footer.edges[0].node.texts
			: null;

	const cookiesText =
		data.cookie.edges &&
		data.cookie.edges[0] &&
		data.cookie.edges[0].node &&
		data.cookie.edges[0].node.texts
			? data.cookie.edges[0].node.texts.value
			: null;

	const cookiesTitle =
		data.cookie.edges &&
		data.cookie.edges[0] &&
		data.cookie.edges[0].node &&
		data.cookie.edges[0].node.subtitle
			? data.cookie.edges[0].node.subtitle
			: null;

	const cookieNoticeExists =
		data.cookie.edges &&
		data.cookie.edges[0] &&
		data.cookie.edges[0].node &&
		data.cookie.edges[0].node.showflag
			? data.cookie.edges[0].node.showflag
			: null;

	/**
	 * format menus data
	 */
	const navData = navDataFormat(data.mainMenu, sitemap_arr);
	const secondaryNavData = navDataFormat(data.secondaryMenu, sitemap_arr);
	const footerNavData = navDataFormat(data.footerMenu, sitemap_arr);
	const privateNavData = navDataFormat(data.privateMenu, sitemap_arr);

	/**
	 * define user status and fetch user data if logged in
	 */
	useEffect(() => {
		(async function () {
			if (!location.pathname.includes("oauth/") && userData === null) {
				let _userData = await fetchUserData(isAuthenticated, dispatch);
				setUserData(_userData);
			}
		})();
	}, [isAuthenticated, dispatch, location]);

	/**
	 * wipe out userData if logged out
	 */
	useEffect(() => {
		
		if (!isAuthenticated && userData !== null) {
			setUserData(null);
		}
	}, [isAuthenticated]);

	/**
	 * check if visitor can see restricted page
	 */

	useEffect(() => {
		if (isRestricted) {
			(async function () {
				let _restricted = await findIfRestricted(pageData, location);
				setIsRestricted(_restricted);
			})();
		}
	}, [location]);

	/**
	 * save cart items on local storage session
	 */

	useEffect(() => {
		if (typeof window !== "undefined") {
			window.localStorage.setItem("cart_items", JSON.stringify(cartItems));
		}
	}, [cartItems]);

	return (
		<SiteContext.Provider
			value={{
				...data.site.siteMetadata,
				configs,
				themeConfig,
				sitemap,
				sitemap_arr,
				isAuthenticated,
				dispatch,
				userData,
				isIframe: _isIframe,
				authContext,
				pageData,
				type: pageData.type,
				isPrivate: pageData.private,
				isPrivateLayout,
				isRestricted,
				navData,
				secondaryNavData,
				footerNavData,
				footerData,
				cookiesText,
				cookiesTitle,
				cookieNoticeExists,
				privateNavData,
				path,
				location,
				bundles,
				layouts,
				typeDefinitions,
				setUserData,
				bookmarkableBundles,
				siteKey,
				sortOptions,
				alertBanners,
				cartItems,
				setCartItems,
				formsConfig: options.formsConfig || {},
				previewing,
				setPreviewing,
				cookieAgreementOnlyinHome:
					!!options.themeConfig?.customOptions?.cookieAgreementOnlyinHome,
			}}
		>
			{element}
		</SiteContext.Provider>
	);
}

export default Site;
